import { useEffect, useState } from 'react';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';

import { useCampaignQueryContext } from '../campaignFolders/contexts/CampaignQueryContext';
import { useSelectionContext } from '../campaignFolders/contexts/SelectionContext';
import { useCampaignFolderModals } from '../hooks/useCampaignFolderModals';
import { useRenameFolder } from '../hooks/useRenameFolder';

export const displayContent = {
  cancel: 'Cancel',
  label: 'Folder name',
  rename: 'Rename folder',
  next: 'Rename folder',
};

type MoveFolderContentModalProps = {
  content?: Record<string, string>;
};

export const RenameFolderModal = ({
  content = displayContent,
  ...props
}: MoveFolderContentModalProps) => {
  const { refetch } = useCampaignQueryContext();
  const { active, clearAll } = useSelectionContext();
  const modals = useCampaignFolderModals();
  const [newFolderName, setNewFolderName] = useState(active[0].name);

  const [renameFolder] = useRenameFolder({
    handleComplete: () => {
      refetch();
      modals.rename.toggle(false);
    },
    handleSuccess: () => {
      refetch();
    },
    folder: active[0],
  });

  useEffect(() => {
    return () => {
      // when an item gets set through the actions, we need to clear it on the way out
      clearAll();
    };
  }, []);

  const handleClose = () => modals.rename.toggle(false);

  return (
    <Modal
      data-test="rename-folder-modal-wrapper"
      id="rename-folder-modal"
      isModalOpen={true}
      // @ts-ignore not typed
      onClose={handleClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
      {...props}
    >
      <ModalHeader data-test="rename-folder-modal-header">{content.rename}</ModalHeader>
      <ModalBody data-test="rename-folder-modal-body" id="rename-folder-modal-body">
        <FormField
          errorText={content.invalidFolderName}
          key="rename-folder-name"
          hasError={false}
          inputId="rename-folder-name"
          labelText={content.label}
        >
          <InputText
            key="field-new-folder-name"
            id="new-folder-name"
            onChange={(nameOfFolder: string) => {
              setNewFolderName(nameOfFolder);
            }}
            type="text"
            value={newFolderName}
          />
        </FormField>
      </ModalBody>
      <ModalFooter
        cancelLabel={content.cancel}
        data-test="rename-folder-modal-actions"
        id="rename-folder-modal-actions"
      >
        <PrimaryButton
          data-test="next-button"
          onClick={() => {
            renameFolder({
              variables: {
                folderId: active[0].id,
                newName: newFolderName,
              },
            });
          }}
          isDisabled={!newFolderName}
          size={BUTTON_SIZES.LARGE}
        >
          {content.next}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
